import React, { useState, useRef, useEffect } from 'react';
import { Calendar, ChevronDown, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isSameDay, addMonths, subMonths, startOfWeek, endOfWeek } from 'date-fns';

type Period = '7d' | '30d' | '90d' | 'custom' | 'today' | 'yesterday' | 'this_week' | 'last_week' | 'this_month' | 'last_month' | 'this_quarter' | 'last_quarter' | 'this_year' | 'last_year';

interface DateRangePickerProps {
  period: Period;
  onPeriodChange: (period: Period) => void;
  customStart: string;
  customEnd: string;
  onCustomStartChange: (date: string) => void;
  onCustomEndChange: (date: string) => void;
  onApply: () => void;
}

const DateRangePicker: React.FC<DateRangePickerProps> = ({
  period,
  onPeriodChange,
  customStart,
  customEnd,
  onCustomStartChange,
  onCustomEndChange,
  onApply,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  const [selectedStartDate, setSelectedStartDate] = useState<Date | null>(null);
  const [selectedEndDate, setSelectedEndDate] = useState<Date | null>(null);
  const [selectingStart, setSelectingStart] = useState(true);
  const [hoveredDate, setHoveredDate] = useState<Date | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const periodOptions: { label: string; value: Period }[] = [
    { label: 'Today', value: 'today' },
    { label: 'Yesterday', value: 'yesterday' },
    { label: 'Last 7 Days', value: '7d' },
    { label: 'Last 30 Days', value: '30d' },
    { label: 'Last 90 Days', value: '90d' },
    { label: 'This Week', value: 'this_week' },
    { label: 'Last Week', value: 'last_week' },
    { label: 'This Month', value: 'this_month' },
    { label: 'Last Month', value: 'last_month' },
    { label: 'This Quarter', value: 'this_quarter' },
    { label: 'Last Quarter', value: 'last_quarter' },
    { label: 'This Year', value: 'this_year' },
    { label: 'Last Year', value: 'last_year' },
    { label: 'Custom Range', value: 'custom' },
  ];

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (customStart) {
      setSelectedStartDate(new Date(`${customStart}T00:00:00`));
    }
    if (customEnd) {
      setSelectedEndDate(new Date(`${customEnd}T00:00:00`));
    }
  }, [customStart, customEnd]);

  const handleDateClick = (date: Date) => {
    const normalizedDate = new Date(date);
    normalizedDate.setHours(0, 0, 0, 0);
    if (selectingStart || !selectedStartDate || normalizedDate < selectedStartDate) {
      setSelectedStartDate(normalizedDate);
      setSelectedEndDate(null);
      setSelectingStart(false);
      setHoveredDate(null);
      onCustomStartChange(format(normalizedDate, 'yyyy-MM-dd'));
      if (period !== 'custom') {
        onPeriodChange('custom');
      }
    } else {
      const normalizedEnd = new Date(date);
      normalizedEnd.setHours(0, 0, 0, 0);
      setSelectedEndDate(normalizedEnd);
      setSelectingStart(true);
      setHoveredDate(null);
      onCustomEndChange(format(normalizedEnd, 'yyyy-MM-dd'));
      if (selectedStartDate) {
        if (period !== 'custom') {
          onPeriodChange('custom');
        }
        onApply();
        setIsOpen(false);
      }
    }
  };

  const getDaysInMonth = () => {
    const monthStart = startOfMonth(calendarMonth);
    const monthEnd = endOfMonth(calendarMonth);
    const calendarStart = startOfWeek(monthStart);
    const calendarEnd = endOfWeek(monthEnd);
    return eachDayOfInterval({ start: calendarStart, end: calendarEnd });
  };

  const getRangeBoundaries = () => {
    const startCandidate = selectedStartDate;
    const endCandidate = selectedEndDate || (!selectingStart ? hoveredDate : null);
    if (!startCandidate && !endCandidate) return { start: null, end: null };
    if (!startCandidate) return { start: endCandidate, end: endCandidate };
    if (!endCandidate) return { start: startCandidate, end: null };
    const startTime = startCandidate.getTime();
    const endTime = endCandidate.getTime();
    if (startTime <= endTime) {
      return { start: startCandidate, end: endCandidate };
    }
    return { start: endCandidate, end: startCandidate };
  };

  const rangeBoundaries = getRangeBoundaries();

  const isDateInRange = (date: Date) => {
    if (!rangeBoundaries.start) return false;
    if (!rangeBoundaries.end) {
      return isSameDay(date, rangeBoundaries.start);
    }
    return date >= rangeBoundaries.start && date <= rangeBoundaries.end;
  };

  const isDateStart = (date: Date) => {
    return rangeBoundaries.start ? isSameDay(date, rangeBoundaries.start) : false;
  };

  const isDateEnd = (date: Date) => {
    return rangeBoundaries.end ? isSameDay(date, rangeBoundaries.end) : false;
  };

  const selectedPeriod = periodOptions.find(opt => opt.value === period);

  const applyPreset = (preset: Period) => {
    const today = new Date();
    let start: Date;
    let end: Date = today;

    switch (preset) {
      case 'today':
        start = today;
        end = today;
        break;
      case 'yesterday':
        start = new Date(today);
        start.setDate(today.getDate() - 1);
        end = start;
        break;
      case '7d':
        start = new Date(today);
        start.setDate(today.getDate() - 6);
        break;
      case '30d':
        start = new Date(today);
        start.setDate(today.getDate() - 29);
        break;
      case '90d':
        start = new Date(today);
        start.setDate(today.getDate() - 89);
        break;
      case 'this_week':
        start = new Date(today);
        const dayOfWeek = today.getDay();
        start.setDate(today.getDate() - dayOfWeek);
        start.setHours(0, 0, 0, 0);
        break;
      case 'last_week':
        start = new Date(today);
        const lastWeekDay = today.getDay();
        start.setDate(today.getDate() - lastWeekDay - 7);
        start.setHours(0, 0, 0, 0);
        end = new Date(start);
        end.setDate(start.getDate() + 6);
        end.setHours(23, 59, 59, 999);
        break;
      case 'this_month':
        start = new Date(today.getFullYear(), today.getMonth(), 1);
        break;
      case 'last_month':
        start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        end = new Date(today.getFullYear(), today.getMonth(), 0);
        break;
      case 'this_quarter':
        const quarter = Math.floor(today.getMonth() / 3);
        start = new Date(today.getFullYear(), quarter * 3, 1);
        break;
      case 'last_quarter':
        const lastQuarter = Math.floor(today.getMonth() / 3) - 1;
        const lastQuarterYear = lastQuarter < 0 ? today.getFullYear() - 1 : today.getFullYear();
        const lastQuarterMonth = lastQuarter < 0 ? 9 : lastQuarter * 3;
        start = new Date(lastQuarterYear, lastQuarterMonth, 1);
        end = new Date(lastQuarterYear, lastQuarterMonth + 3, 0);
        break;
      case 'this_year':
        start = new Date(today.getFullYear(), 0, 1);
        break;
      case 'last_year':
        start = new Date(today.getFullYear() - 1, 0, 1);
        end = new Date(today.getFullYear() - 1, 11, 31);
        break;
      default:
        return;
    }

    onCustomStartChange(format(start, 'yyyy-MM-dd'));
    onCustomEndChange(format(end, 'yyyy-MM-dd'));
    onPeriodChange(preset);
    setIsOpen(false);
  };

  const days = getDaysInMonth();
  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-900 shadow-sm transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#9c68f2] dark:border-white/20 dark:bg-white dark:text-gray-900 dark:hover:bg-white/90 sm:px-3 sm:py-2 sm:text-sm"
      >
        <Calendar className="h-4 w-4" />
        <span className="truncate max-w-[7rem] sm:max-w-none">{selectedPeriod?.label || 'Select Period'}</span>
        <ChevronDown className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div
          className="absolute top-full right-0 z-50 mt-2 overflow-hidden rounded-lg border border-[rgb(var(--border))] bg-white shadow-lg dark:border-[#404040] dark:bg-[#303030]"
          style={{ width: 'auto', maxWidth: 'calc(100vw - 1.5rem)' }}
        >
          <div className="flex flex-col md:flex-row">
            {/* Preset options */}
            <div className="md:min-w-[180px] border-b md:border-b-0 md:border-r border-[rgb(var(--border))] dark:border-[#404040] max-h-80 overflow-y-auto">
              {periodOptions.map((option) => (
                <button
                  key={option.value}
                  onClick={() => {
                    if (option.value === 'custom') {
                      if (period !== 'custom') {
                        onPeriodChange('custom');
                      }
                      setSelectingStart(true);
                    } else {
                      applyPreset(option.value);
                    }
                  }}
                  className={`w-full text-left px-4 py-2 text-sm transition-colors ${
                    period === option.value
                      ? 'bg-gray-100 dark:bg-[#3a3a3a] text-gray-900 dark:text-[rgb(var(--fg))] font-medium'
                      : 'text-gray-700 dark:text-[rgb(var(--muted))] hover:bg-gray-50 dark:hover:bg-[#3a3a3a]'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>

            {/* Calendar */}
            <div className="flex-1 min-w-[320px] p-4">
              <div className="flex items-center justify-between mb-4">
                <button
                  onClick={() => setCalendarMonth(subMonths(calendarMonth, 1))}
                  className="p-1 rounded hover:bg-gray-100 dark:hover:bg-[#3a3a3a] text-gray-700 dark:text-[rgb(var(--muted))]"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <h3 className="text-sm font-semibold text-gray-900 dark:text-[rgb(var(--fg))]">
                  {format(calendarMonth, 'MMMM yyyy')}
                </h3>
                <button
                      onClick={() => setCalendarMonth(addMonths(calendarMonth, 1))}
                  className="p-1 rounded hover:bg-gray-100 dark:hover:bg-[#3a3a3a] text-gray-700 dark:text-[rgb(var(--muted))]"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>

              <div className="grid grid-cols-7 gap-1 mb-2">
                {weekDays.map((day) => (
                  <div key={day} className="text-xs font-medium text-gray-500 dark:text-[rgb(var(--muted))] text-center py-1">
                    {day}
                  </div>
                ))}
              </div>

              <div
                className="grid grid-cols-7 gap-1"
                onMouseLeave={() => {
                  if (!selectingStart) {
                    setHoveredDate(null);
                  }
                }}
              >
                {days.map((day, idx) => {
                  const isCurrentMonth = isSameMonth(day, calendarMonth);
                  const isInRange = isDateInRange(day);
                  const isStart = isDateStart(day);
                  const isEnd = isDateEnd(day);
                  const isToday = isSameDay(day, new Date());

                  return (
                    <button
                      key={idx}
                      onClick={() => handleDateClick(day)}
                      disabled={!isCurrentMonth}
                      onMouseEnter={() => {
                        if (!selectingStart) {
                          const hovered = new Date(day);
                          hovered.setHours(0, 0, 0, 0);
                          setHoveredDate(hovered);
                        }
                      }}
                      className={`
                        h-8 text-xs rounded transition-colors
                        ${!isCurrentMonth ? 'text-[rgb(var(--muted))] dark:text-gray-600' : 'text-gray-900 dark:text-gray-200'}
                        ${isInRange ? 'bg-blue-100 dark:bg-blue-900/30' : ''}
                        ${isStart || isEnd ? 'bg-blue-600 dark:bg-blue-500 text-[rgb(var(--fg))] font-semibold' : ''}
                        ${isToday && !isStart && !isEnd ? 'ring-2 ring-blue-500 dark:ring-blue-400' : ''}
                        ${isCurrentMonth && !isStart && !isEnd ? 'hover:bg-gray-100 dark:hover:bg-[#3a3a3a]' : ''}
                        ${!isCurrentMonth ? 'cursor-not-allowed' : 'cursor-pointer'}
                      `}
                    >
                      {format(day, 'd')}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="border-t border-[rgb(var(--border))] dark:border-[#404040] bg-gray-50 dark:bg-[rgb(var(--card))] px-4 py-3 flex flex-col gap-3">
            <div className="flex items-center justify-between text-xs">
              <div>
                <div className="text-gray-500 dark:text-[rgb(var(--muted))]">From</div>
                <div className="text-gray-900 dark:text-[rgb(var(--fg))] font-medium">
                  {selectedStartDate ? format(selectedStartDate, 'MMM d, yyyy') : 'Select start date'}
                </div>
              </div>
              <div>
                <div className="text-gray-500 dark:text-[rgb(var(--muted))]">To</div>
                <div className="text-gray-900 dark:text-[rgb(var(--fg))] font-medium">
                  {selectedEndDate ? format(selectedEndDate, 'MMM d, yyyy') : 'Select end date'}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => {
                  setSelectedStartDate(null);
                  setSelectedEndDate(null);
                  setSelectingStart(true);
                  setHoveredDate(null);
                  onCustomStartChange('');
                  onCustomEndChange('');
                  onPeriodChange('custom');
                }}
                className="flex-1 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 transition-colors hover:bg-gray-50 dark:border-white/20 dark:bg-white dark:text-gray-900 dark:hover:bg-white/90"
              >
                Reset
              </button>
              <button
                onClick={() => {
                  if (selectedStartDate && selectedEndDate) {
                    onPeriodChange('custom');
                    onApply();
                    setIsOpen(false);
                  }
                }}
                disabled={!selectedStartDate || !selectedEndDate}
                className="flex-1 px-3 py-2 text-sm rounded-lg bg-blue-600 dark:bg-blue-500 text-[rgb(var(--fg))] hover:bg-blue-700 dark:hover:bg-blue-600 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DateRangePicker;
